/**
 * Minimal, dependency-free i18n.
 *
 * - Flat dot-keyed dictionaries per language.
 * - Active locale stored in localStorage under `saas.lang`.
 * - Listeners so components re-render on locale change (no Context needed
 *   for the translator itself — the hook subscribes).
 * - Interpolation: t("greet", { name: "Ada" }) with tokens like {name}.
 * - Missing keys return the key itself (helps spot gaps in dev).
 *
 * Adding a new language later (Spanish / Italian) = drop another dictionary
 * into /locales and append it to LOCALES.
 */
import { useEffect, useState } from "react";
import tr from "./locales/tr";
import en from "./locales/en";
import de from "./locales/de";

export const LOCALES = [
  { code: "tr", label: "Türkçe",   flag: "🇹🇷", dict: tr },
  { code: "en", label: "English",  flag: "🇬🇧", dict: en },
  { code: "de", label: "Deutsch",  flag: "🇩🇪", dict: de },
  // Placeholder slots — user asked for ES/IT to be easy to add later.
  // Drop /locales/es.js, /locales/it.js and uncomment when ready.
  // { code: "es", label: "Español",  flag: "🇪🇸", dict: es },
  // { code: "it", label: "Italiano", flag: "🇮🇹", dict: it },
];

const DEFAULT = "tr";
const STORAGE = "saas.lang";

const listeners = new Set();
let current =
  (typeof window !== "undefined" && localStorage.getItem(STORAGE)) || DEFAULT;

function dictFor(code) {
  return (LOCALES.find((l) => l.code === code) || LOCALES[0]).dict;
}

function interpolate(str, vars) {
  if (!vars) return str;
  return str.replace(/\{(\w+)\}/g, (_, k) => (k in vars ? String(vars[k]) : `{${k}}`));
}

export function getLocale() {
  return current;
}

export function setLocale(code) {
  if (!LOCALES.some((l) => l.code === code)) return;
  current = code;
  try { localStorage.setItem(STORAGE, code); } catch { /* ignore */ }
  try { document.documentElement.lang = code; } catch { /* ignore */ }
  listeners.forEach((fn) => fn(code));
}

export function translate(key, vars) {
  const d = dictFor(current);
  const v = d[key] ?? dictFor(DEFAULT)[key] ?? key;
  return interpolate(v, vars);
}

export function useI18n() {
  const [, setTick] = useState(0);
  useEffect(() => {
    const fn = () => setTick((n) => n + 1);
    listeners.add(fn);
    return () => listeners.delete(fn);
  }, []);
  return {
    t: translate,
    locale: current,
    setLocale,
    locales: LOCALES,
  };
}
